var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/resize.scss';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { leftSideTools, } from '../interface/gameController';
import ButtonComponent from './ButtonComponent';
import { getContentLockArray } from '../helper/helper';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { rotatePointAroundCenter } from '../helper/numberFrameHelper';
import customEventHandler from '../helper/customEvents';
import { t } from 'i18next';
// Synthetic FollowerEngine sourceId for a resizeHolder-driven multi-select move — this
// gesture has no single "source" manipulative, so it can't reuse a real id (a real id
// here would let captureMultiSelectStartPositions's exclude-self check accidentally
// exclude that item instead of just the ones ResizerComponent moves directly).
var RESIZE_HOLDER_SOURCE_ID = '__resizeHolderMultiSelect__';
var ResizerComponent = function () {
    var _a = useState(false), isKeyboardFocused = _a[0], setIsKeyboardFocused = _a[1];
    // Detect when annotation SVG or resizer control gets keyboard focus.
    // Also restores canvas scroll when VoiceOver platform-scrolls to the wrong
    // position (elements inside CSS-transformed containers trigger incorrect AT scroll).
    useEffect(function () {
        var onFocusIn = function () {
            var active = document.activeElement;
            setIsKeyboardFocused(!!(active === null || active === void 0 ? void 0 : active.closest('.raphaelCanvas')) && (active === null || active === void 0 ? void 0 : active.getAttribute('data-focused')) === 'true');
            // Only apply scroll-restore for elements inside the canvas transform.
            var isInResizer = !!(active === null || active === void 0 ? void 0 : active.closest('.resizeHolder'));
            if (!isInResizer)
                return;
            var scrollEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
            if (!scrollEl)
                return;
            var savedLeft = scrollEl.scrollLeft;
            var savedTop = scrollEl.scrollTop;
            var restore = function () {
                scrollEl.scrollLeft = savedLeft;
                scrollEl.scrollTop = savedTop;
            };
            scrollEl.addEventListener('scroll', restore, { once: true });
            setTimeout(function () { return scrollEl.removeEventListener('scroll', restore); }, 500);
        };
        var onFocusOut = function () { return setIsKeyboardFocused(false); };
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        return function () {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
        };
    }, []);
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, annotations = _b.annotations, manipulative = _b.manipulative, table = _b.table, setAnnotations = _b.setAnnotations, setSelectedTool = _b.setSelectedTool, tool = _b.tool, setTool = _b.setTool, text = _b.text, setText = _b.setText, setManipulative = _b.setManipulative, resizerControls = _b.resizerControls, resizerPadding = _b.resizerPadding, resizerRotateMode = _b.resizerRotateMode, onResizerRotateClick = _b.onResizerRotateClick;
    var padding = resizerPadding;
    var controlsVisible = selectedTool.length >= 1;
    // In-frame counters can't be resized and shouldn't show the resizer overlay —
    // it renders outside moveableCanvas at z-index 1000 and would cover the counter during drag.
    var allInFrameCounters = selectedTool.length > 0 &&
        selectedTool.every(function (toolId) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            return (item === null || item === void 0 ? void 0 : item.data.type) === 'numberFrameCounter' && !!item.data.frameId;
        });
    var _c = globalStore.useStoreState(function (st) { return st.playerStore; }), zoomLevel = _c.zoomLevel, canvasHeight = _c.canvasHeight, canvasWidth = _c.canvasWidth;
    var lockState = getContentLockArray({ selectedTool: selectedTool, text: text, table: table, annotations: annotations, manipulative: manipulative });
    var contrlEnabled = useMemo(function () {
        var _a;
        var controls = __spreadArray([], resizerControls, true);
        if (lockState.positioning) {
            controls = controls.filter(function (c) { return c !== 'middle'; });
        }
        if (lockState.editing) {
            // For lines and numberFrames, "editing" lock only restricts resize/placement — rotation is still allowed.
            var isLine = selectedTool.length === 1 &&
                selectedTool[0].startsWith('annotations_') &&
                ((_a = annotations[Number(selectedTool[0].split('_')[1])]) === null || _a === void 0 ? void 0 : _a.type) === 'line';
            var editControls_1 = ['leftTop', 'rightTop', 'leftBottom', 'rightBottom', 'top', 'bottom', 'left', 'right'];
            if (!isLine)
                editControls_1.push('rotate');
            controls = controls.filter(function (c) { return !editControls_1.includes(c); });
        }
        return controls;
    }, [resizerControls, lockState.positioning, lockState.editing, selectedTool, annotations]);
    var _d = useState(false), isDragging = _d[0], setIsDragging = _d[1];
    var isDraggingRef = useRef(false);
    var dragTypeRef = useRef(null);
    var startPos = useRef({ x: 0, y: 0 });
    var prevPos = useRef({ x: 0, y: 0 });
    var oldValues = useRef({});
    // Diagnostic-only: running sum of the incremental dx/dy applied to directly-managed
    // items (annotations/numberFrame/IDM-counter), to compare against the FollowerEngine's
    // total-delta-from-start model and quantify any drift between the two. See Follow-up 3
    // in the resizeHolder drag plan.
    var incrementalSumRef = useRef({ x: 0, y: 0 });
    var driftLogFrameRef = useRef(0);
    // Always-fresh refs for reading current state at drag-end. The drag
    // useEffect's deps only include isDragging, so its onMouseUp closure
    // captures `annotations` / `text` from when the drag started — any
    // setAnnotations/setText updates made during the drag aren't visible.
    // These refs bypass the stale closure so `_new` reflects the true final
    // position. Without this, saveState records {old, new} as identical and
    // redo becomes a no-op (undo still works by bouncing back to `_old`).
    var annotationsRefLatest = useRef(annotations);
    var textRefLatest = useRef(text);
    useEffect(function () {
        annotationsRefLatest.current = annotations;
    }, [annotations]);
    useEffect(function () {
        textRefLatest.current = text;
    }, [text]);
    var mainHolderRectRef = useRef(null);
    var zoomLevelRef = useRef(zoomLevel);
    zoomLevelRef.current = zoomLevel;
    var toNumber = function (v) { return Math.floor(Number(v)) || 0; };
    var getMinMax = function (pt1, pt2) {
        return {
            minX: Math.min((pt1 === null || pt1 === void 0 ? void 0 : pt1.x) || 0, (pt2 === null || pt2 === void 0 ? void 0 : pt2.x) || 0),
            maxX: Math.max((pt1 === null || pt1 === void 0 ? void 0 : pt1.x) || 0, (pt2 === null || pt2 === void 0 ? void 0 : pt2.x) || 0),
            minY: Math.min((pt1 === null || pt1 === void 0 ? void 0 : pt1.y) || 0, (pt2 === null || pt2 === void 0 ? void 0 : pt2.y) || 0),
            maxY: Math.max((pt1 === null || pt1 === void 0 ? void 0 : pt1.y) || 0, (pt2 === null || pt2 === void 0 ? void 0 : pt2.y) || 0),
        };
    };
    var getCoordinates = useMemo(function () {
        var x1 = Infinity, y1 = Infinity, x2 = 0, y2 = 0, rotation = 0;
        selectedTool.forEach(function (_select) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            if (_select.includes('annotations')) {
                var _ann = annotations[Number(_select.split('_')[1])];
                // Selection can hold a stale `annotations_N` index after the
                // backing array shrinks (page change, undo, deletion). Skip
                // gracefully — the next render will clear the selection.
                if (!_ann || !_ann.data)
                    return;
                var _newX = void 0, _newY = void 0, width = void 0, height = void 0;
                if (_ann.type !== 'line') {
                    _newX =
                        toNumber((_a = _ann.data.center) === null || _a === void 0 ? void 0 : _a.x) -
                            toNumber((_b = _ann.data.dimensions) === null || _b === void 0 ? void 0 : _b.width) / 2 -
                            toNumber(_ann.data.thickness) / 2;
                    _newY =
                        toNumber((_c = _ann.data.center) === null || _c === void 0 ? void 0 : _c.y) -
                            toNumber((_d = _ann.data.dimensions) === null || _d === void 0 ? void 0 : _d.height) / 2 -
                            toNumber(_ann.data.thickness) / 2;
                    width = toNumber((_e = _ann.data.dimensions) === null || _e === void 0 ? void 0 : _e.width) + toNumber(_ann.data.thickness);
                    height = toNumber((_f = _ann.data.dimensions) === null || _f === void 0 ? void 0 : _f.height) + toNumber(_ann.data.thickness);
                    if (selectedTool.length == 1) {
                        rotation = _ann.data.rotation || 0;
                    }
                }
                else {
                    var _s = getMinMax((_g = _ann.data.points) === null || _g === void 0 ? void 0 : _g[0], (_h = _ann.data.points) === null || _h === void 0 ? void 0 : _h[1]), minX = _s.minX, minY = _s.minY, maxX = _s.maxX, maxY = _s.maxY;
                    _newX = minX - _ann.data.thickness / 2;
                    _newY = minY - _ann.data.thickness / 2;
                    width = maxX - minX + _ann.data.thickness;
                    height = maxY - minY + _ann.data.thickness;
                    if (selectedTool.length == 1) {
                        rotation = _ann.data.rotation || 0;
                    }
                }
                x1 = Math.min(x1, _newX - padding);
                y1 = Math.min(y1, _newY - padding);
                x2 = Math.max(x2, _newX + width + padding);
                y2 = Math.max(y2, _newY + height + padding);
            }
            if (_select.includes('text') || _select.includes('newMath')) {
                var _ann = text[Number(_select.split('_')[1])];
                if (!_ann || !_ann.data)
                    return;
                var _newX = void 0, _newY = void 0, width = void 0, height = void 0;
                var posX = toNumber((_j = _ann.data.position) === null || _j === void 0 ? void 0 : _j.x);
                var posY = toNumber((_k = _ann.data.position) === null || _k === void 0 ? void 0 : _k.y);
                width = toNumber(_ann.data.lineWidth || 0);
                height = toNumber(_ann.data.height || 0);
                // bounding box top-left
                _newX = posX;
                _newY = posY;
                if (selectedTool.length === 1) {
                    rotation = _ann.data.rotation || 0;
                }
                width = toNumber((_l = _ann.data) === null || _l === void 0 ? void 0 : _l.lineWidth);
                height = toNumber((_m = _ann.data) === null || _m === void 0 ? void 0 : _m.height);
                if (selectedTool.length == 1) {
                    rotation = _ann.data.rotation || 0;
                }
                x1 = Math.min(x1, _newX - width / 2 - padding);
                y1 = Math.min(y1, _newY - height / 2 - padding);
                x2 = Math.max(x2, _newX + width / 2 + padding);
                y2 = Math.max(y2, _newY + height / 2 + padding);
            }
            // IDM tray counters — derive bounds from center + dimensions so the
            // resizer actually has coordinates to render at. Without this branch
            // the default Infinity/0 coords collapse the overlay to nothing.
            var item = manipulative.find(function (m) { return m.data.id === _select; });
            if ((item === null || item === void 0 ? void 0 : item.data.type) === 'counter' && item.data.customImage) {
                var counter = item.data;
                var cx = toNumber((_o = counter.center) === null || _o === void 0 ? void 0 : _o.x);
                var cy = toNumber((_p = counter.center) === null || _p === void 0 ? void 0 : _p.y);
                var w = toNumber((_q = counter.dimensions) === null || _q === void 0 ? void 0 : _q.width) || 60;
                var h = toNumber((_r = counter.dimensions) === null || _r === void 0 ? void 0 : _r.height) || 60;
                var counterRot = counter.rotation || 0;
                if (selectedTool.length === 1) {
                    // Single select: the resizeHolder itself rotates (via CSS transform) to
                    // match the counter, so use the natural (unrotated) dimensions here.
                    rotation = counterRot;
                    var _newX = cx - w / 2;
                    var _newY = cy - h / 2;
                    x1 = Math.min(x1, _newX - padding);
                    y1 = Math.min(y1, _newY - padding);
                    x2 = Math.max(x2, _newX + w + padding);
                    y2 = Math.max(y2, _newY + h + padding);
                }
                else {
                    // Multi-select: the resizeHolder is always axis-aligned, so compute
                    // each counter's rotation-aware AABB to ensure nothing overflows.
                    var rad = (counterRot * Math.PI) / 180;
                    var cosR = Math.abs(Math.cos(rad));
                    var sinR = Math.abs(Math.sin(rad));
                    var aabbW = w * cosR + h * sinR;
                    var aabbH = w * sinR + h * cosR;
                    var _newX = cx - aabbW / 2;
                    var _newY = cy - aabbH / 2;
                    x1 = Math.min(x1, _newX - padding);
                    y1 = Math.min(y1, _newY - padding);
                    x2 = Math.max(x2, _newX + aabbW + padding);
                    y2 = Math.max(y2, _newY + aabbH + padding);
                }
            }
        });
        return { x1: x1, y1: y1, x2: x2, y2: y2, rotation: rotation };
    }, [selectedTool, annotations, text, manipulative, padding]);
    useEffect(function () {
        var click = function (e) {
            // Don't clear selection while dragging/rotating
            if (isDraggingRef.current) {
                return;
            }
            var target = e.target;
            // Don't clear selectedTool if clicking on buttons, sidebar, collapsible divs, or math keyboard
            if (target.tagName === 'BUTTON' ||
                target.closest('button') ||
                target.closest('.sidebar') ||
                target.closest('.collapsibleRight') ||
                target.closest('.collapsibleOptionHolder') ||
                target.closest('.mathEditorParent') ||
                target.closest('.keypadWrapper')) {
                return;
            }
            if (target === null || target === void 0 ? void 0 : target.classList.contains('drawingCanvas')) {
                setSelectedTool([]);
            }
        };
        document === null || document === void 0 ? void 0 : document.addEventListener('click', click);
        return function () { return document === null || document === void 0 ? void 0 : document.removeEventListener('click', click); };
    }, [isDragging]);
    // --- MOUSE DOWN HANDLER FOR MOVING / RESIZING ---
    var handleMouseDown = function (e, type) {
        var _a;
        if (type === void 0) { type = null; }
        // Prevent default behavior to avoid focus change and page refresh/scrolling
        e.preventDefault();
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        e.stopPropagation();
        document.body.style.userSelect = 'none';
        isDraggingRef.current = true;
        dragTypeRef.current = type;
        setIsDragging(true);
        // Cache mainHolder rect once on drag start to avoid layout thrashing during drag
        var mainHolder = document.getElementsByClassName('mainHolder')[0];
        mainHolderRectRef.current = mainHolder ? mainHolder.getBoundingClientRect() : null;
        startPos.current = { x: clientX, y: clientY };
        prevPos.current = { x: clientX, y: clientY };
        incrementalSumRef.current = { x: 0, y: 0 };
        driftLogFrameRef.current = 0;
        selectedTool.forEach(function (tool) {
            if (tool.includes('annotations')) {
                var idx = Number(tool.split('_')[1]);
                oldValues.current["annotations_".concat(idx)] = JSON.stringify(__assign({}, annotations[idx]));
            }
            else if (tool.includes('text')) {
                var idx = Number(tool.split('_')[1]);
                oldValues.current["text_".concat(idx)] = JSON.stringify(__assign({}, text[idx]));
            }
            else if (tool.startsWith('numberFrame_')) {
                var item = manipulative.find(function (m) { return m.data.id === tool; });
                if (item)
                    oldValues.current[tool] = JSON.stringify(item.data);
            }
            else {
                // IDM tray counter (type 'counter' + customImage) — capture for undo
                var item = manipulative.find(function (m) { return m.data.id === tool; });
                if ((item === null || item === void 0 ? void 0 : item.data.type) === 'counter' && item.data.customImage) {
                    oldValues.current[tool] = JSON.stringify(item.data);
                }
            }
        });
        // oldValues.current = {
        //   annotations: annotations.map(ann => ({ ...ann })),
        //   text: text.map(t => ({ ...t })),
        // };
        // Multi-select move: dragging the resizeHolder's middle/border-edge zone is a group
        // move gesture, same as dragging any single selected manipulative directly. Become the
        // FollowerEngine leader for every OTHER selected item, since this component already
        // moves annotations/numberFrame/IDM-counter items itself (see processDrag below) —
        // exclude those from the follower engine so their delta isn't applied twice.
        if (type === 'middle' && selectedTool.length > 1) {
            var directlyManagedIds = selectedTool.filter(function (tool) {
                if (tool.includes('annotations') || tool.includes('text'))
                    return true;
                if (tool.startsWith('numberFrame_'))
                    return true;
                var item = manipulative.find(function (m) { return m.data.id === tool; });
                return (item === null || item === void 0 ? void 0 : item.data.type) === 'counter' && !!item.data.customImage;
            });
            var targetEl = e.target;
            // eslint-disable-next-line no-console
            console.log('[ResizeHolderDrag debug] handleMouseDown middle dispatch', JSON.stringify({
                target: "".concat((_a = targetEl === null || targetEl === void 0 ? void 0 : targetEl.tagName) !== null && _a !== void 0 ? _a : '', ".").concat(typeof (targetEl === null || targetEl === void 0 ? void 0 : targetEl.className) === 'string' ? targetEl.className : ''),
                selectedTool: __spreadArray([], selectedTool, true),
                directlyManagedIds: directlyManagedIds,
            }));
            customEventHandler.dispatchEvent('multiSelectMoveStart', {
                sourceId: RESIZE_HOLDER_SOURCE_ID,
                excludeIds: directlyManagedIds,
            });
        }
    };
    var rafId = useRef(0);
    useEffect(function () {
        var onMouseMove = function (e) {
            // Prevent default touch behavior to avoid page refresh/scrolling on iPad
            e.preventDefault();
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            if (!isDraggingRef.current)
                return;
            // Throttle: only process once per animation frame
            if (rafId.current)
                cancelAnimationFrame(rafId.current);
            rafId.current = requestAnimationFrame(function () {
                rafId.current = 0;
                processDrag(clientX, clientY);
            });
        };
        var processDrag = function (clientX, clientY) {
            var dragType = dragTypeRef.current;
            var currentZoom = zoomLevelRef.current;
            var dx = (clientX - prevPos.current.x) / currentZoom;
            var dy = (clientY - prevPos.current.y) / currentZoom;
            prevPos.current = { x: clientX, y: clientY };
            if (dragType === 'middle' && selectedTool.length > 1) {
                // Diagnostic-only: incrementalSumRef tracks the running sum of the per-frame dx/dy
                // the directly-managed branch below is about to add (incremental model) — compared
                // against the FollowerEngine's total-delta-from-start model on the same tick.
                incrementalSumRef.current = {
                    x: incrementalSumRef.current.x + dx,
                    y: incrementalSumRef.current.y + dy,
                };
                var followerTotalDeltaX = (clientX - startPos.current.x) / currentZoom;
                var followerTotalDeltaY = (clientY - startPos.current.y) / currentZoom;
                customEventHandler.dispatchEvent('multiSelectMove', {
                    sourceId: RESIZE_HOLDER_SOURCE_ID,
                    deltaX: followerTotalDeltaX,
                    deltaY: followerTotalDeltaY,
                });
                // Logged every 10th frame to avoid flooding the console.
                driftLogFrameRef.current += 1;
                if (driftLogFrameRef.current % 10 === 0) {
                    // eslint-disable-next-line no-console
                    console.log('[ResizeHolderDrag debug] middle-drag drift check (incremental vs total-delta model)', JSON.stringify({
                        frame: driftLogFrameRef.current,
                        incrementalSumSoFar: incrementalSumRef.current,
                        followerTotalDelta: { x: followerTotalDeltaX, y: followerTotalDeltaY },
                        driftX: incrementalSumRef.current.x - followerTotalDeltaX,
                        driftY: incrementalSumRef.current.y - followerTotalDeltaY,
                    }));
                }
            }
            var hasAnnotations = selectedTool.some(function (s) { return s.includes('annotations'); });
            var hasText = selectedTool.some(function (s) { return s.includes('text') || s.includes('newMath'); });
            if (hasAnnotations)
                setAnnotations(function (prev) {
                    var updated = __spreadArray([], prev, true);
                    selectedTool.forEach(function (_select) {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
                        if (!_select.includes('annotations'))
                            return;
                        var idx = Number(_select.split('_')[1]);
                        var ann = __assign(__assign({}, updated[idx]), { data: __assign({}, updated[idx].data) });
                        var _r = ann.data.rotation, rotation = _r === void 0 ? 0 : _r;
                        var cos = Math.cos((rotation * Math.PI) / 180);
                        var sin = Math.sin((rotation * Math.PI) / 180);
                        var localDx = dx * cos + dy * sin;
                        var localDy = -dx * sin + dy * cos;
                        if (dragType === 'middle') {
                            if (ann.type === 'line') {
                                ann.data.points = (_a = ann.data.points) === null || _a === void 0 ? void 0 : _a.map(function (p) { return ({
                                    x: toNumber(p.x) + dx,
                                    y: toNumber(p.y) + dy,
                                }); });
                            }
                            else {
                                ann.data.center = {
                                    x: toNumber((_b = ann.data.center) === null || _b === void 0 ? void 0 : _b.x) + dx,
                                    y: toNumber((_c = ann.data.center) === null || _c === void 0 ? void 0 : _c.y) + dy,
                                };
                            }
                            updated[idx] = ann;
                        }
                        else if (ann.type !== 'line') {
                            var _s = ann.data.dimensions || {}, _t = _s.width, width = _t === void 0 ? 0 : _t, _u = _s.height, height = _u === void 0 ? 0 : _u;
                            var _v = ann.data.center || {}, x = _v.x, y = _v.y;
                            var newwidth = width || 0;
                            var newheight = height || 0;
                            x = x || 0;
                            y = y || 0;
                            switch (dragType) {
                                case 'leftTop':
                                    newwidth -= localDx;
                                    newheight -= localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'rightTop':
                                    newwidth += localDx;
                                    newheight -= localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'leftBottom':
                                    newwidth -= localDx;
                                    newheight += localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'rightBottom':
                                    newwidth += localDx;
                                    newheight += localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'top':
                                    newheight -= localDy;
                                    y += (localDy * cos) / 2;
                                    x -= (localDy * sin) / 2;
                                    break;
                                case 'bottom':
                                    newheight += localDy;
                                    y += (localDy * cos) / 2;
                                    x -= (localDy * sin) / 2;
                                    break;
                                case 'left':
                                    newwidth -= localDx;
                                    y += (localDx * sin) / 2;
                                    x += (localDx * cos) / 2;
                                    break;
                                case 'right':
                                    newwidth += localDx;
                                    y += (localDx * sin) / 2;
                                    x += (localDx * cos) / 2;
                                    break;
                                case 'rotation': {
                                    var _rect = (_d = mainHolderRectRef.current) !== null && _d !== void 0 ? _d : (_e = document.getElementsByClassName('mainHolder')[0]) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect();
                                    if (!_rect)
                                        break;
                                    var angle = Math.atan2(clientY - _rect.y - y * currentZoom, clientX - _rect.x - x * currentZoom);
                                    var degrees = (angle * 180) / Math.PI + 90;
                                    ann.data.rotation = (degrees + 360) % 360;
                                    break;
                                }
                            }
                            newwidth = Math.max(5, newwidth);
                            newheight = Math.max(5, newheight);
                            ann.data.dimensions = { width: newwidth, height: newheight };
                            ann.data.center = { x: x, y: y };
                            updated[idx] = ann;
                        }
                        else {
                            // resizing/rotating for lines — mirror non-line shape approach
                            var p0x = Number((_g = (_f = ann.data.points) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.x) || 0;
                            var p0y = Number((_j = (_h = ann.data.points) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.y) || 0;
                            var p1x = Number((_l = (_k = ann.data.points) === null || _k === void 0 ? void 0 : _k[1]) === null || _l === void 0 ? void 0 : _l.x) || 0;
                            var p1y = Number((_o = (_m = ann.data.points) === null || _m === void 0 ? void 0 : _m[1]) === null || _o === void 0 ? void 0 : _o.y) || 0;
                            var leftIdx = p0x <= p1x ? 0 : 1;
                            var rightIdx = p0x <= p1x ? 1 : 0;
                            switch (dragType) {
                                case 'left':
                                case 'right': {
                                    if (ann.data.points) {
                                        var targetIdx = dragType === 'left' ? leftIdx : rightIdx;
                                        var rad = (rotation * Math.PI) / 180;
                                        var c = Math.cos(rad);
                                        var s = Math.sin(rad);
                                        var midX = (p0x + p1x) / 2;
                                        var midY = (p0y + p1y) / 2;
                                        // Forward-rotate stored endpoints → visual positions
                                        var vx0 = midX + (p0x - midX) * c - (p0y - midY) * s;
                                        var vy0 = midY + (p0x - midX) * s + (p0y - midY) * c;
                                        var vx1 = midX + (p1x - midX) * c - (p1y - midY) * s;
                                        var vy1 = midY + (p1x - midX) * s + (p1y - midY) * c;
                                        // Unit vector along the visual line axis
                                        var vlen = Math.hypot(vx1 - vx0, vy1 - vy0);
                                        if (vlen < 1)
                                            break;
                                        var vdx = (vx1 - vx0) / vlen;
                                        var vdy = (vy1 - vy0) / vlen;
                                        // Project cursor delta onto the visual axis (axis-constrained: no
                                        // angle change on perpendicular drag, 1:1 sync along the axis)
                                        var proj = dx * vdx + dy * vdy;
                                        var newVx = [vx0, vx1];
                                        var newVy = [vy0, vy1];
                                        newVx[targetIdx] += proj * vdx;
                                        newVy[targetIdx] += proj * vdy;
                                        var arrowCount = (((_p = ann.data.arrowheadStyle) === null || _p === void 0 ? void 0 : _p.start) === 'arrow' ? 1 : 0) +
                                            (((_q = ann.data.arrowheadStyle) === null || _q === void 0 ? void 0 : _q.end) === 'arrow' ? 1 : 0);
                                        var minDist = arrowCount > 0 ? arrowCount * 40 : 30;
                                        if (Math.hypot(newVx[1] - newVx[0], newVy[1] - newVy[0]) < minDist)
                                            break;
                                        var newMidX = (newVx[0] + newVx[1]) / 2;
                                        var newMidY = (newVy[0] + newVy[1]) / 2;
                                        // Inverse-rotate visual positions back to stored coordinates.
                                        // IMPORTANT: create new point objects — do NOT mutate ann.data.points[n]
                                        // in-place. React StrictMode double-invokes state updaters; mutating the
                                        // shared reference would apply the delta twice (2× movement).
                                        ann.data.points = [
                                            {
                                                x: newMidX + (newVx[0] - newMidX) * c + (newVy[0] - newMidY) * s,
                                                y: newMidY - (newVx[0] - newMidX) * s + (newVy[0] - newMidY) * c,
                                            },
                                            {
                                                x: newMidX + (newVx[1] - newMidX) * c + (newVy[1] - newMidY) * s,
                                                y: newMidY - (newVx[1] - newMidX) * s + (newVy[1] - newMidY) * c,
                                            },
                                        ];
                                    }
                                    break;
                                }
                                case 'rotation':
                                    if (ann.data.points) {
                                        // Use the midpoint of the line as the rotation center
                                        var cx = (p0x + p1x) / 2;
                                        var cy = (p0y + p1y) / 2;
                                        var _rect = document
                                            .getElementsByClassName('mainHolder')[0]
                                            .getBoundingClientRect();
                                        var angle = Math.atan2(clientY - _rect.y - cy * currentZoom, clientX - _rect.x - cx * currentZoom);
                                        var degrees = (angle * 180) / Math.PI + 90;
                                        ann.data.rotation = (degrees + 360) % 360;
                                    }
                                    break;
                            }
                            updated[idx] = ann;
                        }
                    });
                    return updated;
                });
            if (hasText)
                setText(function (prev) {
                    var updated = __spreadArray([], prev, true);
                    selectedTool.forEach(function (_select) {
                        var _a, _b, _c;
                        if (!_select.includes('text') && !_select.includes('newMath'))
                            return;
                        var idx = Number(_select.split('_')[1]);
                        if (!updated[idx] || !updated[idx].data)
                            return;
                        var item = __assign(__assign({}, updated[idx]), { data: __assign({}, updated[idx].data) });
                        var _d = item.data.rotation, rotation = _d === void 0 ? 0 : _d;
                        var cos = Math.cos((rotation * Math.PI) / 180);
                        var sin = Math.sin((rotation * Math.PI) / 180);
                        var localDx = dx * cos + dy * sin;
                        var localDy = -dx * sin + dy * cos;
                        var width = item.data.lineWidth || 0;
                        var height = item.data.height || 0;
                        var newwidth = width || 0;
                        var newheight = height || 0;
                        var pos = (_a = item.data.position) !== null && _a !== void 0 ? _a : { x: 0, y: 0, z: 0 };
                        var x = pos.x;
                        var y = pos.y;
                        var z = pos.z;
                        if (dragType === 'middle') {
                            x += dx;
                            y += dy;
                        }
                        else {
                            switch (dragType) {
                                case 'leftTop':
                                    newwidth -= localDx;
                                    newheight -= localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'rightTop':
                                    newwidth += localDx;
                                    newheight -= localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'leftBottom':
                                    newwidth -= localDx;
                                    newheight += localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'rightBottom':
                                    newwidth += localDx;
                                    newheight += localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'top':
                                    newheight -= localDy;
                                    y += (localDy * cos) / 2;
                                    x -= (localDy * sin) / 2;
                                    break;
                                case 'bottom':
                                    newheight += localDy;
                                    y += (localDy * cos) / 2;
                                    x -= (localDy * sin) / 2;
                                    break;
                                case 'left':
                                    newwidth -= localDx;
                                    y += (localDx * sin) / 2;
                                    x += (localDx * cos) / 2;
                                    break;
                                case 'right':
                                    newwidth += localDx;
                                    y += (localDx * sin) / 2;
                                    x += (localDx * cos) / 2;
                                    break;
                                case 'rotation': {
                                    var _rect = (_b = mainHolderRectRef.current) !== null && _b !== void 0 ? _b : (_c = document.getElementsByClassName('mainHolder')[0]) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
                                    if (!_rect)
                                        break;
                                    var angle = Math.atan2(clientY - _rect.y - y * currentZoom, clientX - _rect.x - x * currentZoom);
                                    var degrees = (angle * 180) / Math.PI + 90;
                                    item.data.rotation = (degrees + 360) % 360;
                                    break;
                                }
                            }
                        }
                        width = Math.max(10, newwidth);
                        height = Math.max(10, newheight);
                        item.data.lineWidth = width;
                        item.data.height = height;
                        item.data.position = { x: x, y: y, z: z };
                        updated[idx] = item;
                    });
                    return updated;
                });
            // NumberFrame rotation
            var hasNumberFrame = selectedTool.some(function (s) { return s.startsWith('numberFrame_'); });
            if (hasNumberFrame && dragType === 'rotation') {
                setManipulative(function (prev) {
                    var updated = __spreadArray([], prev, true);
                    selectedTool.forEach(function (_select) {
                        var _a, _b;
                        if (!_select.startsWith('numberFrame_'))
                            return;
                        var frameIdx = updated.findIndex(function (m) { return m.data.id === _select && m.data.type === 'numberFrames'; });
                        if (frameIdx === -1)
                            return;
                        var nf = updated[frameIdx].data;
                        var _rect = (_a = mainHolderRectRef.current) !== null && _a !== void 0 ? _a : (_b = document.getElementsByClassName('mainHolder')[0]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                        if (!_rect)
                            return;
                        var angle = Math.atan2(clientY - _rect.y - nf.center.y * currentZoom, clientX - _rect.x - nf.center.x * currentZoom);
                        var degrees = (angle * 180) / Math.PI + 90;
                        var newRotation = (degrees + 360) % 360;
                        // Normalize delta to [-180, 180] to handle the 0/360 boundary
                        var rotationDelta = newRotation - (nf.rotation || 0);
                        if (rotationDelta > 180)
                            rotationDelta -= 360;
                        if (rotationDelta < -180)
                            rotationDelta += 360;
                        // Update frame rotation
                        updated[frameIdx] = __assign(__assign({}, updated[frameIdx]), { data: __assign(__assign({}, nf), { rotation: newRotation }) });
                        // Update attached counters: rotate centers and token rotation
                        // (calculateTokenPosition already uses frame.rotation for position,
                        //  but we update center for counters not in frame.tokens map,
                        //  and rotation so token images visually rotate with the frame)
                        updated = updated.map(function (item) {
                            if (item.data.type === 'numberFrameCounter') {
                                var counter = item.data;
                                if (counter.frameId === _select) {
                                    var rotatedPos = rotatePointAroundCenter(counter.center, nf.center, rotationDelta);
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: rotatedPos, rotation: newRotation }) });
                                }
                            }
                            return item;
                        });
                    });
                    return updated;
                });
            }
            // IDM tray counters (type 'counter' + customImage) — full resizer:
            // rotation, corner + edge grips, and middle-drag reposition. Mirrors the
            // text/annotation resize math so rotation-aware drags stay intuitive.
            var counterIds = selectedTool.filter(function (id) {
                var item = manipulative.find(function (m) { return m.data.id === id; });
                return (item === null || item === void 0 ? void 0 : item.data.type) === 'counter' && !!item.data.customImage;
            });
            if (counterIds.length > 0 && dragType) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a, _b, _c, _d, _e, _f;
                        if (!item.data.id || !counterIds.includes(item.data.id))
                            return item;
                        var counter = item.data;
                        var _g = counter.rotation, rotation = _g === void 0 ? 0 : _g;
                        var cos = Math.cos((rotation * Math.PI) / 180);
                        var sin = Math.sin((rotation * Math.PI) / 180);
                        var localDx = dx * cos + dy * sin;
                        var localDy = -dx * sin + dy * cos;
                        var _h = counter.dimensions || {}, _j = _h.width, width = _j === void 0 ? 60 : _j, _k = _h.height, height = _k === void 0 ? 60 : _k;
                        var x = (_b = (_a = counter.center) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0;
                        var y = (_d = (_c = counter.center) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0;
                        var newWidth = width;
                        var newHeight = height;
                        if (dragType === 'middle') {
                            x += dx;
                            y += dy;
                        }
                        else if (dragType === 'rotation') {
                            var _rect = (_e = mainHolderRectRef.current) !== null && _e !== void 0 ? _e : (_f = document.getElementsByClassName('mainHolder')[0]) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect();
                            if (!_rect)
                                return item;
                            var angle = Math.atan2(clientY - _rect.y - y * currentZoom, clientX - _rect.x - x * currentZoom);
                            var degrees = (angle * 180) / Math.PI + 90;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { rotation: (degrees + 360) % 360 }) });
                        }
                        else {
                            switch (dragType) {
                                case 'leftTop':
                                    newWidth -= localDx;
                                    newHeight -= localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'rightTop':
                                    newWidth += localDx;
                                    newHeight -= localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'leftBottom':
                                    newWidth -= localDx;
                                    newHeight += localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'rightBottom':
                                    newWidth += localDx;
                                    newHeight += localDy;
                                    x += (localDx * cos) / 2 - (localDy * sin) / 2;
                                    y += (localDx * sin) / 2 + (localDy * cos) / 2;
                                    break;
                                case 'top':
                                    newHeight -= localDy;
                                    y += (localDy * cos) / 2;
                                    x -= (localDy * sin) / 2;
                                    break;
                                case 'bottom':
                                    newHeight += localDy;
                                    y += (localDy * cos) / 2;
                                    x -= (localDy * sin) / 2;
                                    break;
                                case 'left':
                                    newWidth -= localDx;
                                    y += (localDx * sin) / 2;
                                    x += (localDx * cos) / 2;
                                    break;
                                case 'right':
                                    newWidth += localDx;
                                    y += (localDx * sin) / 2;
                                    x += (localDx * cos) / 2;
                                    break;
                            }
                            newWidth = Math.max(10, newWidth);
                            newHeight = Math.max(10, newHeight);
                        }
                        return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: { x: x, y: y }, dimensions: { width: newWidth, height: newHeight } }) });
                    });
                });
            }
        };
        var onMouseUp = function () {
            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
                rafId.current = 0;
            }
            if (dragTypeRef.current === 'middle' && selectedTool.length > 1) {
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: RESIZE_HOLDER_SOURCE_ID });
            }
            isDraggingRef.current = false;
            dragTypeRef.current = null;
            mainHolderRectRef.current = null;
            setIsDragging(false);
            document.body.style.userSelect = '';
            var entries = [];
            selectedTool.forEach(function (tool) {
                var _a;
                if (tool.startsWith('numberFrame_'))
                    return;
                if (!oldValues.current[tool])
                    return;
                // IDM tray counter rotation: capture end state by id, not by list index.
                var counterItem = manipulative.find(function (m) { return m.data.id === tool; });
                if ((counterItem === null || counterItem === void 0 ? void 0 : counterItem.data.type) === 'counter' && counterItem.data.customImage) {
                    entries.push({
                        type: counterItem.data.id,
                        id: (_a = counterItem.data.id.split('_')[1]) !== null && _a !== void 0 ? _a : counterItem.data.id,
                        propertyName: 'completeData',
                        old: oldValues.current[tool],
                        new: JSON.stringify(counterItem.data),
                    });
                    return;
                }
                var type = tool.includes('annotations') ? 'annotations' : 'text';
                var _old = JSON.parse(oldValues.current[tool]), _new = type === 'annotations'
                    ? annotationsRefLatest.current[Number(tool.split('_')[1])]
                    : textRefLatest.current[Number(tool.split('_')[1])];
                entries.push({
                    type: type,
                    id: tool.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(_old),
                    new: JSON.stringify(_new),
                });
            });
            if (entries.length > 0 && !registerMultiSelectSaveEntries(RESIZE_HOLDER_SOURCE_ID, entries)) {
                saveState(entries);
            }
        };
        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchmove', onMouseMove, { passive: false });
            window.addEventListener('touchend', onMouseUp);
            window.addEventListener('touchcancel', onMouseUp);
        }
        return function () {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchmove', onMouseMove);
            window.removeEventListener('touchend', onMouseUp);
            window.removeEventListener('touchcancel', onMouseUp);
        };
    }, [isDragging]);
    useEffect(function () {
        if (controlsVisible && tool === 'pen') {
            setTool(leftSideTools.selection);
        }
    }, [controlsVisible]);
    useEffect(function () {
        var leftTop = document.querySelector('.leftTop');
        if (leftTop) {
            leftTop.addEventListener('mouseDown', function (e) {
                return handleMouseDown(e, 'leftTop');
            });
            leftTop.addEventListener('touchStart', function (e) {
                return handleMouseDown(e, 'leftTop');
            });
        }
        return function () {
            if (leftTop) {
                leftTop.removeEventListener('mouseDown', function (e) {
                    return handleMouseDown(e, 'leftTop');
                });
                leftTop.removeEventListener('touchStart', function (e) {
                    return handleMouseDown(e, 'leftTop');
                });
            }
        };
    }, []);
    var x1 = getCoordinates.x1, y1 = getCoordinates.y1, x2 = getCoordinates.x2, y2 = getCoordinates.y2, rotation = getCoordinates.rotation;
    if (resizerControls.length === 0)
        return _jsx(_Fragment, {});
    var handleRotateKeyDown = function (e) {
        var clockwise = e.key === 'ArrowRight' || e.key === 'ArrowUp';
        var anticlockwise = e.key === 'ArrowLeft' || e.key === 'ArrowDown';
        if (!clockwise && !anticlockwise)
            return;
        e.preventDefault();
        e.stopPropagation();
        var step = e.shiftKey ? 15 : 1;
        var delta = clockwise ? step : -step;
        // Rotate annotations
        setAnnotations(function (prev) {
            var updated = __spreadArray([], prev, true);
            selectedTool.forEach(function (_select) {
                if (!_select.startsWith('annotations_'))
                    return;
                var idx = Number(_select.split('_')[1]);
                if (!updated[idx])
                    return;
                var ann = __assign(__assign({}, updated[idx]), { data: __assign({}, updated[idx].data) });
                var oldRot = ann.data.rotation || 0;
                ann.data.rotation = (oldRot + delta + 360) % 360;
                updated[idx] = ann;
            });
            return updated;
        });
        // Rotate manipulatives (numberFrames, etc.)
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (!item.data.id || !selectedTool.includes(item.data.id))
                    return item;
                var oldRot = item.data.rotation || 0;
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rotation: (oldRot + delta + 360) % 360 }) });
            });
        });
        // Rotate text
        setText(function (prev) {
            var updated = __spreadArray([], prev, true);
            selectedTool.forEach(function (_select) {
                var _a;
                if (!_select.includes('text') && !_select.includes('newMath'))
                    return;
                var idx = Number(_select.split('_')[1]);
                if (!((_a = updated[idx]) === null || _a === void 0 ? void 0 : _a.data))
                    return;
                var oldRot = updated[idx].data.rotation || 0;
                updated[idx] = __assign(__assign({}, updated[idx]), { data: __assign(__assign({}, updated[idx].data), { rotation: (oldRot + delta + 360) % 360 }) });
            });
            return updated;
        });
    };
    var handleRotateKeyUp = function (e) {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key))
            return;
        // Save state for undo/redo
        selectedTool.forEach(function (_select) {
            if (_select.startsWith('annotations_')) {
                var idx = Number(_select.split('_')[1]);
                var ann = annotations[idx];
                if (ann) {
                    saveState({
                        type: 'annotations',
                        id: String(idx),
                        propertyName: 'completeData',
                        old: oldValues.current[_select] || JSON.stringify(ann),
                        new: JSON.stringify(ann),
                    });
                }
            }
        });
    };
    // NumberFrames has its own inline rotate handle — skip the global resizer for them
    var allNumberFrames = selectedTool.length > 0 && selectedTool.every(function (t) { return t.startsWith('numberFrame_'); });
    // LinearPiece never contributes to getCoordinates (no matching branch), so the
    // overlay either collapses or lands at a stale bounds. It's also not user-
    // resizable — scale is driven by the snapped tile, not by drag handles.
    // Skip the global resizer when the selection contains only LinearPieces.
    var allLinearPieces = selectedTool.length > 0 && selectedTool.every(function (t) { return t.startsWith('linearPiece_'); });
    // Tables have their own resize handles — skip the global resizer overlay
    var allTables = selectedTool.length > 0 && selectedTool.every(function (t) { return t.startsWith('tableText_'); });
    if (allInFrameCounters || allNumberFrames || allLinearPieces || allTables)
        return null;
    return (_jsxs("div", { className: "resizeHolder".concat(isKeyboardFocused ? ' keyboardFocused' : '').concat(selectedTool.length > 1 ? ' multiSelect' : ''), style: {
            left: x1,
            top: y1,
            width: x2 - x1,
            height: y2 - y1,
            cursor: lockState.positioning ? 'default' : isDragging && dragTypeRef.current === 'middle' ? 'grabbing' : 'grab',
            transform: "rotate(".concat(rotation, "deg)"),
            position: 'absolute',
            '--resizer-border-width': "".concat(Math.max(1, Math.round(1 / zoomLevel)), "px"),
        }, onMouseDown: selectedTool.length > 1 && !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined, onTouchStart: selectedTool.length > 1 && !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined, children: [_jsx("div", { className: "borderEdge borderEdgeTop", onMouseDown: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined, onTouchStart: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined }), _jsx("div", { className: "borderEdge borderEdgeBottom", onMouseDown: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined, onTouchStart: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined }), _jsx("div", { className: "borderEdge borderEdgeLeft", onMouseDown: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined, onTouchStart: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined }), _jsx("div", { className: "borderEdge borderEdgeRight", onMouseDown: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined, onTouchStart: !lockState.positioning ? function (e) { return handleMouseDown(e, 'middle'); } : undefined }), controlsVisible && (_jsxs(_Fragment, { children: [contrlEnabled.includes('rotate') && (_jsxs("div", { className: "rotate", children: [resizerRotateMode === 'click' ? (_jsx(ButtonComponent, { imgSrc: {
                                    normal: './assets/images/icons/resetN.svg',
                                    hover: './assets/images/icons/resetH.svg',
                                }, normalCSS: "rotateIcon", clickHandler: onResizerRotateClick, tabIndex: -1 })) : (_jsx(ButtonComponent, { imgSrc: {
                                    normal: './assets/images/icons/resetN.svg',
                                    hover: './assets/images/icons/resetH.svg',
                                }, normalCSS: "rotateIcon", clickHandler: onResizerRotateClick, onMouseDown: function (e) { return handleMouseDown(e, 'rotation'); }, onTouchStart: function (e) { return handleMouseDown(e, 'rotation'); }, onKeyDown: handleRotateKeyDown, onKeyUp: handleRotateKeyUp, tabIndex: 0, role: "slider", ariaLabel: t('text.rotateHandle'), ariaValueNow: Math.round(rotation), ariaValueMin: 0, ariaValueMax: 359 })), _jsx("div", { className: "rotateLine" })] })), contrlEnabled.includes('leftTop') && (_jsx("div", { className: "leftTop", onMouseDown: function (e) { return handleMouseDown(e, 'leftTop'); }, onTouchStart: function (e) { return handleMouseDown(e, 'leftTop'); } })), contrlEnabled.includes('top') && (_jsx("div", { className: "top", onMouseDown: function (e) { return handleMouseDown(e, 'top'); }, onTouchStart: function (e) { return handleMouseDown(e, 'top'); } })), contrlEnabled.includes('rightTop') && (_jsx("div", { className: "rightTop", onMouseDown: function (e) { return handleMouseDown(e, 'rightTop'); }, onTouchStart: function (e) { return handleMouseDown(e, 'rightTop'); } })), contrlEnabled.includes('left') && (_jsx("div", { className: "left", onMouseDown: function (e) { return handleMouseDown(e, 'left'); }, onTouchStart: function (e) { return handleMouseDown(e, 'left'); } })), contrlEnabled.includes('middle') && (_jsx("div", { className: "middle", onMouseDown: function (e) { return handleMouseDown(e, 'middle'); }, onTouchStart: function (e) { return handleMouseDown(e, 'middle'); } })), contrlEnabled.includes('right') && (_jsx("div", { className: "right", onMouseDown: function (e) { return handleMouseDown(e, 'right'); }, onTouchStart: function (e) { return handleMouseDown(e, 'right'); } })), contrlEnabled.includes('leftBottom') && (_jsx("div", { className: "leftBottom", onMouseDown: function (e) { return handleMouseDown(e, 'leftBottom'); }, onTouchStart: function (e) { return handleMouseDown(e, 'leftBottom'); } })), contrlEnabled.includes('bottom') && (_jsx("div", { className: "bottom", onMouseDown: function (e) { return handleMouseDown(e, 'bottom'); }, onTouchStart: function (e) { return handleMouseDown(e, 'bottom'); } })), contrlEnabled.includes('rightBottom') && (_jsx("div", { className: "rightBottom", onMouseDown: function (e) { return handleMouseDown(e, 'rightBottom'); }, onTouchStart: function (e) { return handleMouseDown(e, 'rightBottom'); } }))] }))] }));
};
export default ResizerComponent;
